import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import type { ModuleKey } from '@gty/common';

import { LoginPage } from '../auth/LoginPage';
import PrintPage from '../print/PrintPage';
import { RequireAuth } from '../auth/RequireAuth';
import { RequirePermission } from '../auth/RequirePermission';
import { ComingSoon } from '../pages/ComingSoon';
import { NotFound } from '../pages/NotFound';
import { AppShell } from '../shell/AppShell';
import { DEFAULT_ROUTE, MODULE_REGISTRY } from './module-registry';

/**
 * Which modules have a real screen.
 *
 * This map is the single source of truth for "is it built" — the registry does
 * not carry an `implemented` flag, because two places recording the same fact
 * is two places to forget. A module absent here renders `<ComingSoon/>`, still
 * inside its real guard.
 */
type Screen = LazyExoticComponent<ComponentType>;

const SCREENS: Partial<Record<ModuleKey, Screen>> = {
  DASHBOARD: lazy(() => import('../dashboard/DashboardPage')),
  LEAD_CRM: lazy(() => import('../leads/LeadsPage')),
  CUSTOMER_CRM: lazy(() => import('../customers/CustomersPage')),
  TOUR_MASTER: lazy(() => import('../tours/ToursPage')),
  BOOKING_MANAGEMENT: lazy(() => import('../bookings/BookingsPage')),
  INVOICE_GENERATOR: lazy(() => import('../invoices/InvoicesPage')),
  BOOKING_VOUCHER: lazy(() => import('../vouchers/VouchersPage')),
  CREDIT_NOTES: lazy(() => import('../credit-notes/CreditNotesPage')),
  PAYMENTS_RECEIPTS: lazy(() => import('../payments/PaymentsPage')),
  EXPENSE_TRACKING: lazy(() => import('../expenses/ExpensesPage')),
  REPORTS_ANALYTICS: lazy(() => import('../reports/ReportsPage')),
  SETTINGS_ROLES: lazy(() => import('../settings/SettingsPage')),
  NOTIFICATIONS: lazy(() => import('../notifications/NotificationsPage')),
  WEBSITE: lazy(() => import('../website/WebsitePage')),
};

/**
 * Child routes under a module's path. They inherit the parent's guard, so a
 * detail screen can never be reachable when its list screen is not.
 *
 * Static segments are listed alongside dynamic ones — React Router ranks a
 * literal above a param, so `/bookings/new` resolves to the wizard rather than
 * being read as a booking with the id "new".
 */
const CHILD_SCREENS: Partial<Record<ModuleKey, Array<{ path: string; element: Screen }>>> = {
  LEAD_CRM: [{ path: ':leadId', element: lazy(() => import('../leads/LeadDetailPage')) }],
  CUSTOMER_CRM: [
    { path: ':customerId', element: lazy(() => import('../customers/CustomerDetailPage')) },
  ],
  TOUR_MASTER: [{ path: ':tourId', element: lazy(() => import('../tours/TourDetailPage')) }],
  BOOKING_MANAGEMENT: [
    { path: 'new', element: lazy(() => import('../bookings/BookingWizard')) },
    { path: ':bookingId', element: lazy(() => import('../bookings/BookingDetailPage')) },
  ],
  INVOICE_GENERATOR: [
    { path: ':invoiceId', element: lazy(() => import('../invoices/InvoiceDocumentPage')) },
  ],
};

/**
 * Routes are GENERATED from the module registry (spec §7.3).
 *
 * Every one of the modules gets a real route wrapped in its real guard,
 * including those that only render `<ComingSoon/>`. That makes the guard
 * behaviour of the whole matrix live and testable now, rather than arriving
 * untested alongside each future screen.
 */
export function AppRouter(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/*
        OUTSIDE `RequireAuth` on purpose. Headless Chromium has no session — it
        carries a 60-second token bound to one document, which `/print-data`
        validates server-side. Putting this inside the auth gate would bounce
        the renderer to /login and produce a PDF of the login form.
      */}
      <Route path="/print/:type/:id" element={<PrintPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route index element={<Navigate to={DEFAULT_ROUTE} replace />} />

          {MODULE_REGISTRY.map((entry) => {
            const Screen = SCREENS[entry.module];
            const children = CHILD_SCREENS[entry.module] ?? [];

            const guard = (node: JSX.Element): JSX.Element => (
              <RequirePermission module={entry.module} level={entry.minLevel}>
                {node}
              </RequirePermission>
            );

            return (
              <Route key={entry.path} path={entry.path}>
                <Route
                  index
                  element={guard(Screen !== undefined ? <Screen /> : <ComingSoon module={entry.module} />)}
                />
                {children.map(({ path, element: Child }) => (
                  <Route key={path} path={path} element={guard(<Child />)} />
                ))}
              </Route>
            );
          })}

          {/* Inside the shell, so a mistyped URL keeps its nav and top bar. */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}
